/**
 * AssistantPalette — responsive AI assistant modal.
 *
 *  Layout: ChatGPT / Claude style — compact header at top, scrollable
 *  conversation in the middle, input pinned at the bottom.
 *
 *  Desktop (≥640px) — Centered spotlight modal (Linear / Notion AI vibe).
 *  Mobile  (<640px)  — Bottom-sheet that slides up, full width, rounded top
 *                      corners. Input stays above the iOS home indicator.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, Loader2, Send, Sparkles, X } from 'lucide-react';

import { wabotApi } from '../../api/wabot';
import { formatApiError } from '../../api/errors';
import { useAssistant, type AssistantTurn } from './AssistantContext';

const SUGGESTIONS: string[] = [
  'How many drives this year?',
  'Top 5 PPO offers',
  'Pending JD verifications',
  'Top recruiting companies by selections',
  'Summary of the Biziverse drive',
  'Which student has the highest stipend?',
];

function useIsMobile() {
  const [mobile, setMobile] = useState(
    typeof window === 'undefined' ? false : window.innerWidth < 640,
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}


export function AssistantPalette() {
  const { open, setOpen, history, addTurn, clearHistory } = useAssistant();
  const isMobile = useIsMobile();

  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-focus on open
  useEffect(() => {
    if (open) {
      const delay = isMobile ? 250 : 80;
      const t = setTimeout(() => inputRef.current?.focus(), delay);
      return () => clearTimeout(t);
    }
    setInput('');
  }, [open, isMobile]);

  // Scroll to bottom anchor after every new turn or while asking
  // useLayoutEffect fires after DOM paint → scrollHeight is correct
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history.length, asking]);

  const submit = async (q: string) => {
    const question = q.trim();
    if (!question || asking) return;
    setAsking(true);
    setInput('');

    const askedAt = Date.now();
    try {
      const { data } = await wabotApi.ask(question);
      const turn: AssistantTurn = {
        id: `${askedAt}`,
        question,
        answer: data.reply,
        tool_calls: data.tool_calls,
        latency_ms: data.latency_ms,
        asked_at: askedAt,
      };
      addTurn(turn);
    } catch (err) {
      addTurn({
        id: `${askedAt}`,
        question,
        answer: '',
        tool_calls: [],
        latency_ms: 0,
        asked_at: askedAt,
        error: formatApiError(err),
      });
    } finally {
      setAsking(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    } else if (e.key === 'ArrowUp' && input === '' && history.length) {
      e.preventDefault();
      setInput(history[history.length - 1].question);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const sheetVariants = isMobile
    ? {
        initial: { y: '100%', opacity: 1 },
        animate: { y: 0, opacity: 1 },
        exit:    { y: '100%', opacity: 1 },
      }
    : {
        initial: { y: -10, opacity: 0, scale: 0.97 },
        animate: { y: 0,   opacity: 1, scale: 1 },
        exit:    { y: -8,  opacity: 0, scale: 0.97 },
      };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 bg-surface-950/50 sm:bg-surface-950/40 backdrop-blur-sm"
            aria-hidden="true"
          />

          {/* Sheet alignment — mobile: bottom, desktop: 12vh from top */}
          <div
            className={[
              'fixed inset-0 z-50 flex justify-center pointer-events-none',
              'items-end sm:items-start',
              'p-0 sm:p-4 sm:pt-[12vh]',
            ].join(' ')}
          >
            <motion.div
              role="dialog"
              aria-label="AI assistant"
              variants={sheetVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: isMobile ? 0.28 : 0.18, ease: isMobile ? [0.32, 0.72, 0, 1] : 'easeOut' }}
              className={[
                'pointer-events-auto w-full flex flex-col',
                'rounded-t-3xl sm:max-w-2xl sm:rounded-2xl',
                'bg-white dark:bg-surface-900',
                'border border-surface-200 dark:border-surface-800',
                'border-b-0 sm:border-b',
                'shadow-2xl shadow-violet-500/10',
                'overflow-hidden',
                'max-h-[85vh] sm:max-h-[76vh]',
              ].join(' ')}
            >
              {/* ── Mobile drag handle ─────────────────────────────── */}
              <div className="flex sm:hidden justify-center pt-2.5 pb-1 shrink-0">
                <div className="h-1 w-10 rounded-full bg-surface-300 dark:bg-surface-700" />
              </div>

              {/* ── Header — branding + clear + close ─────────────── */}
              <div className="shrink-0 flex items-center gap-2.5 px-4 py-3 border-b border-surface-100 dark:border-surface-800">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-md">
                  <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-surface-900 dark:text-surface-50 leading-none">
                    Portal Assistant
                  </p>
                  <p className="text-[11px] text-surface-400 dark:text-surface-500 mt-0.5">
                    Gemini Flash · live portal data
                  </p>
                </div>
                {history.length > 0 && (
                  <button
                    type="button"
                    onClick={clearHistory}
                    className="text-[11px] text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 px-2 py-1 rounded-md hover:bg-surface-100 dark:hover:bg-surface-800"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={[
                    'shrink-0 flex items-center justify-center',
                    'h-8 w-8 rounded-xl',
                    'text-surface-400 hover:bg-surface-100 hover:text-surface-700',
                    'dark:hover:bg-surface-800 dark:hover:text-surface-200',
                  ].join(' ')}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* ── Conversation body — scrollable ─────────────────── */}
              <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-3 min-h-0">
                {history.length === 0 && !asking && (
                  <EmptySuggestions onPick={submit} />
                )}

                {history.map((t) => (
                  <TurnRow key={t.id} turn={t} />
                ))}

                {asking && <AskingTrace />}

                {/* Scroll anchor — always at the very bottom */}
                <div ref={bottomRef} />
              </div>

              {/* ── Input area — pinned at bottom ─────────────────── */}
              <div
                className={[
                  'shrink-0 border-t border-surface-100 dark:border-surface-800',
                  'px-3 pt-3 pb-3',
                  // Respect iPhone home indicator
                  'pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
                ].join(' ')}
              >
                {/* Input row */}
                <div className={[
                  'flex items-end gap-2',
                  'rounded-2xl border',
                  'border-surface-200 dark:border-surface-700',
                  'bg-surface-50 dark:bg-surface-800',
                  'px-3 py-2',
                  'focus-within:border-violet-400 dark:focus-within:border-violet-500',
                  'focus-within:ring-2 focus-within:ring-violet-400/20',
                  'transition-all',
                ].join(' ')}>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={asking}
                    rows={1}
                    placeholder="Ask anything about drives, placements, students…"
                    enterKeyHint="send"
                    className={[
                      'flex-1 resize-none bg-transparent text-base leading-snug',
                      'text-surface-900 dark:text-surface-50',
                      'placeholder:text-surface-400 dark:placeholder:text-surface-500',
                      'focus:outline-none',
                      'disabled:opacity-60',
                      'min-h-[1.5rem] max-h-32 overflow-y-auto',
                      'py-0.5',
                    ].join(' ')}
                    style={{ fieldSizing: 'content' } as React.CSSProperties}
                  />
                  <button
                    type="button"
                    onClick={() => submit(input)}
                    disabled={!input.trim() || asking}
                    aria-label="Send"
                    className={[
                      'shrink-0 flex h-8 w-8 items-center justify-center rounded-xl',
                      'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white',
                      'shadow-md shadow-violet-500/30',
                      'transition-all',
                      'hover:shadow-lg hover:scale-[1.04]',
                      'active:scale-[0.95]',
                      'disabled:opacity-40 disabled:scale-100 disabled:shadow-none',
                      'mb-0.5',
                    ].join(' ')}
                  >
                    {asking
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
                    }
                  </button>
                </div>

                {/* Keyboard hints — desktop only */}
                <p className="hidden sm:block text-[11px] text-surface-400 dark:text-surface-500 mt-1.5 px-1">
                  Enter to send · Shift+Enter for new line · ↑ to recall last
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}


// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────

function EmptySuggestions({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div>
      <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-surface-400 mb-2 px-1">
        Try asking
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className={[
              'group flex items-center gap-2 rounded-xl px-3 py-2.5 sm:py-2 text-left text-sm',
              'text-surface-700 dark:text-surface-200',
              'hover:bg-violet-50 dark:hover:bg-violet-500/10',
              'hover:text-violet-700 dark:hover:text-violet-300',
              'active:bg-violet-100 dark:active:bg-violet-500/20',
              'transition-colors',
            ].join(' ')}
          >
            <ArrowUp className="h-3.5 w-3.5 rotate-45 text-surface-400 group-hover:text-violet-500 shrink-0" />
            <span className="truncate">{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


function TurnRow({ turn }: { turn: AssistantTurn }) {
  const [traceOpen, setTraceOpen] = useState(false);
  return (
    <div className="space-y-2">
      {/* Question — user side (right) */}
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-surface-100 dark:bg-surface-800 px-3 py-2 text-sm text-surface-800 dark:text-surface-100">
          {turn.question}
        </div>
      </div>

      {/* Answer — AI side (left) */}
      <div className="flex justify-start">
        <div className="max-w-[92%] sm:max-w-[88%] space-y-1.5">
          {turn.error ? (
            <div className="rounded-2xl rounded-tl-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              {turn.error.includes('busy') || turn.error.includes('rate') || turn.error.includes('429')
                ? '⏳ AI is busy — wait a few seconds and ask again.'
                : turn.error.includes('reach') || turn.error.includes('503') || turn.error.includes('network')
                ? '📡 Can\'t reach the AI right now — try again in a moment.'
                : turn.error}
            </div>
          ) : (
            <div className="rounded-2xl rounded-tl-sm bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-500/10 dark:to-fuchsia-500/10 border border-violet-100 dark:border-violet-500/20 px-3.5 py-2.5 text-sm text-surface-900 dark:text-surface-100 whitespace-pre-wrap leading-relaxed">
              {turn.answer || '(empty answer)'}
            </div>
          )}

          {turn.tool_calls.length > 0 && (
            <button
              type="button"
              onClick={() => setTraceOpen((v) => !v)}
              className="text-[11px] text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 px-1 -mx-1 py-0.5 rounded"
            >
              {traceOpen
                ? '▾ hide details'
                : `▸ used ${turn.tool_calls.length} tool${turn.tool_calls.length === 1 ? '' : 's'} · ${turn.latency_ms} ms`}
            </button>
          )}
          {traceOpen && (
            <div className="mt-1 rounded-lg bg-surface-50 dark:bg-surface-950/60 border border-surface-200 dark:border-surface-800 px-2.5 py-1.5 space-y-1 font-mono text-[11px] text-surface-500 dark:text-surface-400 overflow-x-auto">
              {turn.tool_calls.map((tc, i) => (
                <div key={i} className="whitespace-nowrap">
                  → {tc.name}({JSON.stringify(tc.arguments)})
                  {tc.error && <span className="text-red-500"> · error: {tc.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function AskingTrace() {
  return (
    <div className="flex justify-start">
      <div className="rounded-2xl rounded-tl-sm bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20 px-3.5 py-2.5 text-sm text-violet-700 dark:text-violet-300 flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Thinking…
      </div>
    </div>
  );
}
